import { type ReactNode, useState } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TablePagination,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CompactTableColumn<T> {
  /** Unique key for the column */
  key: string;
  /** Header label (string or ReactNode for custom content like hint icons) */
  label: ReactNode;
  /** Width — CSS grid column size (e.g., "1fr", "120px", "80px") */
  width: string;
  /** Text alignment for both header and cells */
  align?: "left" | "right" | "center";
  /** Render function for the cell content */
  render: (record: T) => ReactNode;
}

interface CompactTableProps<T extends { id: string }> {
  /** Data to display */
  data: T[];
  /** Column definitions */
  columns: CompactTableColumn<T>[];
  /** Generate a link URL for each row (makes rows clickable) */
  rowLink?: (record: T) => string;
  /** Rows per page (default: 10) */
  rowsPerPage?: number;
  /** Empty state message */
  emptyMessage?: string;
  /** Enable row selection with checkboxes */
  selectable?: boolean;
  /** Currently selected IDs (controlled) */
  selectedIds?: string[];
  /** Selection change callback */
  onSelectionChange?: (ids: string[]) => void;
  /** Toolbar to show above the table when items are selected */
  selectionToolbar?: ReactNode;
  /** Whether to show the header row (default: true) */
  showHeader?: boolean;
  /** Render function for expandable row detail */
  expandRow?: (record: T) => ReactNode;
  /** Optional function to determine if a row can be selected (default: all selectable) */
  isSelectable?: (record: T) => boolean;
  /** Optional render function for content next to expand chevron (e.g., item count badge) */
  expandLabel?: (record: T) => ReactNode;
  /** Optional predicate — when provided and returns false, the expand chevron is
   * suppressed for that row (and the panel won't render). The chevron column is
   * still reserved so rows stay aligned. Defaults to always-expandable when
   * `expandRow` is set. */
  isExpandable?: (record: T) => boolean;
  /** Optional per-row sx override (e.g. faded styling for excluded rows).
   * Merged after the table's own background/hover styles, so callers control
   * opacity, color, etc. without losing selection/hover affordances. */
  getRowSx?: (record: T) => SxProps<Theme>;
  /** When true, the table sizes to its content width instead of stretching
   * to fill its parent. Empty space (if any) lands on the right of the
   * table; horizontal scroll still kicks in when columns exceed parent
   * width. Used by nested tables (e.g. inside an expand panel) where a
   * full-width table looks bloated. */
  fitContent?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const CompactTable = <T extends { id: string }>({
  data,
  columns,
  rowLink,
  rowsPerPage = 10,
  emptyMessage = "No records found",
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  selectionToolbar,
  showHeader = true,
  expandRow,
  isSelectable,
  expandLabel,
  isExpandable,
  getRowSx,
  fitContent = false,
}: CompactTableProps<T>) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const paginatedData = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const showPagination = data.length > rowsPerPage;

  // Selection helpers — only consider selectable rows
  const selectablePageIds = paginatedData.filter((r) => !isSelectable || isSelectable(r)).map((r) => r.id);
  const allPageSelected = selectablePageIds.length > 0 && selectablePageIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allPageSelected;

  const handleToggleAll = () => {
    if (!onSelectionChange) return;
    if (allPageSelected) {
      onSelectionChange(selectedIds.filter((id) => !selectablePageIds.includes(id)));
    } else {
      onSelectionChange([...new Set([...selectedIds, ...selectablePageIds])]);
    }
  };

  const handleToggleRow = (id: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Build grid template with optional expand + checkbox columns
  const expandCol = expandRow ? (expandLabel ? "48px " : "32px ") : "";
  const checkboxCol = selectable ? "36px " : "";
  const gridTemplate = expandCol + checkboxCol + columns.map((c) => c.width).join(" ");

  return (
    // Outer Box pins the table to its parent's width and renders the
    // selection toolbar OUTSIDE the horizontally-scrolling Paper so the
    // toolbar's actions (Exclude / Restore) stay visible at the natural
    // column width even when the table itself is scrolled to the right.
    <Box sx={{ width: fitContent ? "fit-content" : "100%", maxWidth: "100%" }}>
      {selectable && selectedIds.length > 0 && selectionToolbar}
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          width: fitContent ? "fit-content" : "100%",
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <Box sx={{ minWidth: "fit-content" }}>
          {/* Header row */}
          {showHeader && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridTemplate,
                gap: 1.5,
                px: 2,
                py: 0.75,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
                alignItems: "center",
              }}
            >
              {expandRow && <Box />}
              {selectable && (
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={someSelected}
                  onChange={handleToggleAll}
                  sx={{ p: 0 }}
                />
              )}
              {columns.map((col) => (
                <Typography
                  key={col.key}
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textAlign: col.align || "left" }}
                >
                  {col.label}
                </Typography>
              ))}
            </Box>
          )}

          {/* Empty state */}
          {data.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
              <Typography variant="body2" color="text.secondary">
                {emptyMessage}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Data rows */}
              <Stack divider={<Divider />}>
                {paginatedData.map((record) => {
                  const isSelected = selectedIds.includes(record.id);
                  const isExpanded = expandedIds.has(record.id);

                  const customRowSx = getRowSx ? getRowSx(record) : undefined;
                  const rowContent = (
                    <Box
                      sx={[
                        {
                          display: "grid",
                          gridTemplateColumns: gridTemplate,
                          gap: 1.5,
                          alignItems: "center",
                          px: 2,
                          py: 1,
                          transition: "background-color 0.15s, opacity 0.15s",
                          bgcolor: isSelected ? "action.selected" : "transparent",
                          "&:hover": { bgcolor: isSelected ? "action.selected" : "action.hover" },
                          ...(rowLink && !expandRow && { textDecoration: "none", color: "inherit", cursor: "pointer" }),
                        },
                        ...(customRowSx ? (Array.isArray(customRowSx) ? customRowSx : [customRowSx]) : []),
                      ]}
                    >
                      {expandRow && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                          {!isExpandable || isExpandable(record) ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleToggleExpand(record.id);
                                }}
                                sx={{ p: 0 }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowDown fontSize="small" />
                                ) : (
                                  <KeyboardArrowRight fontSize="small" />
                                )}
                              </IconButton>
                              {expandLabel && expandLabel(record)}
                            </>
                          ) : null}
                        </Box>
                      )}
                      {selectable &&
                        (() => {
                          const rowSelectable = !isSelectable || isSelectable(record);
                          return (
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              disabled={!rowSelectable}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleRow(record.id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ p: 0 }}
                            />
                          );
                        })()}
                      {columns.map((col) => (
                        <Box key={col.key} sx={{ textAlign: col.align || "left", minWidth: 0 }}>
                          {col.render(record)}
                        </Box>
                      ))}
                    </Box>
                  );

                  return (
                    <Box key={record.id}>
                      {rowLink && !expandRow ? (
                        <Box
                          component={RouterLink}
                          to={rowLink(record)}
                          sx={{ textDecoration: "none", color: "inherit" }}
                        >
                          {rowContent}
                        </Box>
                      ) : (
                        <Box
                          onClick={() => rowLink && navigate(rowLink(record))}
                          sx={{ cursor: rowLink ? "pointer" : "default" }}
                        >
                          {rowContent}
                        </Box>
                      )}
                      {/* Expand panel — skipped entirely when the row isn't expandable. */}
                      {expandRow && (!isExpandable || isExpandable(record)) && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ pl: 8, pr: 2, pb: 1.5, pt: 0.5, bgcolor: "action.hover" }}>
                            {expandRow(record)}
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {/* Pagination */}
              {showPagination && (
                <>
                  <Divider />
                  <TablePagination
                    component="div"
                    count={data.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[rowsPerPage]}
                    sx={{
                      ".MuiTablePagination-toolbar": { minHeight: 36 },
                      ".MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
                    }}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
